// src/workflow/JsonFormRenderer.jsx
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box, Divider, Heading, HStack, IconButton,
  Stack, Switch, Text, Textarea, Tooltip, Alert, AlertIcon
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import FormRenderer from "./FormRenderer";

export default function JsonFormRenderer() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [readOnly, setReadOnly] = useState(true);

  // Parse the JSON string that was passed in navigation state
  let initialData = null;
  let parseError = null;
  const jsonString = location?.state?.jsonString;

  try {
    initialData = jsonString ? JSON.parse(jsonString) : null;
  } catch (err) {
    parseError = err.message;
  }

  const [formData, setFormData] = useState(initialData);

  const updatedPreview = useMemo(() => {
    try { return JSON.stringify(formData ?? {}, null, 2); } catch { return ""; }
  }, [formData]);

  return (
    <Box maxW="1000px" mx="auto" p={6}>
      <HStack justify="space-between" mb={3}>
        <HStack>
          <Tooltip label="Back">
            <IconButton aria-label="Back" icon={<ArrowBackIcon />} onClick={() => navigate(-1)} />
          </Tooltip>
          <Heading size="md">Workflow Payload</Heading>
        </HStack>
        <HStack>
          <Text fontSize="sm" color="gray.600">ID: {id}</Text>
          <HStack>
            <Text fontSize="sm">Edit</Text>
            <Switch isChecked={!readOnly} onChange={(e) => setReadOnly(!e.target.checked)} />
          </HStack>
        </HStack>
      </HStack>

      <Divider mb={4} />

      {!jsonString && (
        <Alert status="warning" mb={4}>
          <AlertIcon />
          No JSON string was provided to this route. Navigate here via “View Payload” from the list.
        </Alert>
      )}

      {parseError && (
        <Alert status="error" mb={4}><AlertIcon />Invalid JSON: {parseError}</Alert>
      )}

      {!parseError && formData && (
        <Stack spacing={6}>
          <Box>
            <Heading size="sm" mb={2}>Form</Heading>
            <Box borderWidth="1px" borderRadius="lg" p={4}>
              <FormRenderer data={formData} onChange={setFormData} readOnly={readOnly} />
            </Box>
          </Box>

          <Box>
            <Heading size="sm" mb={2}>JSON</Heading>
            <Textarea value={updatedPreview} readOnly rows={12} fontFamily="mono" />
          </Box>
        </Stack>
      )}
    </Box>
  );
}
